import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";

import { fetchPublicAPI } from "hooks/usePublicAPI";
import {
  type SavedQuoteResponse,
  type AvailableAddOn,
  type PricingPlan,
  type QuotePackage,
  getPreferencePackage,
  getOtherPackages,
} from "./helpers";
import { formatCurrency } from "helpers/currency";
import { capitalizeFirstLetter } from "helpers/string";
import PreferencePackageCard from "./PreferencePackageCard";
import OtherPackageCard from "./OtherPackageCard";
import FixedFooterBar, { type FooterPlacement } from "./FixedFooterBar";
import PriceBreakdownModal from "./PriceBreakdownModal";

// A viewport-pinned bar floats over the page, so the page keeps room for it
// below its content. A panel bar sits in the flow at the bottom of the
// panel, so the quote fills the panel and the bar ends it with nothing under.
const ROOT_CLASS: Record<FooterPlacement, string> = {
  viewport: "bg-white pb-20",
  panel: "flex flex-1 flex-col bg-white",
};

/**
 * Add-on Item (name and price only)
 */
function AddOnItem({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex items-center justify-between rounded-md bg-oat p-6">
      <span className="font-serif text-xl italic text-evergreen">{label}</span>
      <span className="text-xl font-bold text-evergreen">
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

/**
 * Quote Details View
 */
export default function QuoteView({
  data,
  footerPlacement = "viewport",
}: {
  data: SavedQuoteResponse;
  footerPlacement?: FooterPlacement;
}) {
  const customerName = data.customer_first_name
    ? capitalizeFirstLetter(data.customer_first_name)
    : "there";
  const decedentName = data.quote_context?.decedent_first_name
    ? capitalizeFirstLetter(data.quote_context.decedent_first_name)
    : undefined;
  const isV2Market = data.quote_context?.is_v2_market ?? false;

  const preferencePackage = getPreferencePackage(
    data.packages,
    isV2Market,
    data.selected_package,
  );
  const otherPackages = getOtherPackages(data.packages, preferencePackage);
  const preferenceName = preferencePackage?.external_plan_name || "";

  // Fetch available add-ons from API
  const [availableAddOns, setAvailableAddOns] = useState<AvailableAddOn[]>([]);
  // Fetch pricing plans for inclusion_highlights
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  // Price breakdown modal state
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);

  useEffect(() => {
    if (!data.dcd_location) return;

    const fetchAddOns = async () => {
      try {
        const response = await fetchPublicAPI<{
          status: string;
          offerings: AvailableAddOn[];
        }>(
          `/pricing/offerings?address=${encodeURIComponent(data.dcd_location!)}&business_segment=At-Need&category=ADD_ON`,
        );
        if (response.status === "success" && response.offerings) {
          setAvailableAddOns(response.offerings);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { operation: "fetch_available_add_ons" },
        });
      }
    };

    const fetchPlans = async () => {
      try {
        const response = await fetchPublicAPI<{
          status: string;
          plans: PricingPlan[];
        }>(
          `/pricing/plans?address=${encodeURIComponent(data.dcd_location!)}&business_segment=At-Need`,
        );
        if (response.status === "success" && response.plans) {
          setPricingPlans(response.plans);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { operation: "fetch_pricing_plans" },
        });
      }
    };

    fetchAddOns();
    fetchPlans();
  }, [data.dcd_location]);

  // Find plan by variant_id for accessing all plan data (subtitle, tagline, highlights)
  const getPlanForPackage = (pkg: QuotePackage): PricingPlan | undefined => {
    return pricingPlans.find((p) => p.variant_id === pkg.plan_variant_id);
  };

  // Get selected add-ons from the breakdown for filtering
  const selectedAddOns = (
    preferencePackage?.breakdown.additional_services || []
  ).filter((s) => (s.total_quantity ?? 0) > 0 || (s.total_fee ?? 0) > 0);

  // Build sets for matching: variant_ids for new quotes, titles as fallback for older quotes
  const selectedVariantIds = new Set(
    selectedAddOns.filter((s) => s.variant_id).map((s) => s.variant_id),
  );
  const selectedTitles = new Set(
    selectedAddOns.map((s) => (s.label || s.name).toLowerCase()),
  );

  // Filter out already selected add-ons (prefer variant_id match, fallback to title match)
  const additionalServices = availableAddOns.filter((addon) => {
    // If we have variant_id matches, use that (more robust)
    if (
      selectedVariantIds.size > 0 &&
      selectedVariantIds.has(addon.variant_id)
    ) {
      return false;
    }
    // Fallback: match by title (for older quotes without variant_id)
    return !selectedTitles.has(addon.title.toLowerCase());
  });

  return (
    <div className={ROOT_CLASS[footerPlacement]}>
      {/* Main Content */}
      <div className="mx-auto w-full max-w-lg pb-12 pt-6">
        {/* Welcome Header */}
        <h1 className="mb-6 text-center font-serif text-[32px] italic leading-tight text-soil">
          Welcome, {customerName}
        </h1>

        {/* Hero Section */}
        <div className="mb-6 space-y-2 px-3">
          <p className="text-lg font-bold text-soil">
            {decedentName
              ? `Quote for ${decedentName}'s arrangements`
              : "Your quote"}
          </p>
          <p className="text-base text-soil">
            We've highlighted the {preferenceName} package based on what you've
            shared.
          </p>
          <p className="text-base text-soil">
            You can begin {decedentName ? `${decedentName}'s ` : ""}arrangements
            now, and finalize your package later. No pressure.
          </p>
        </div>

        {/* Preference Package */}
        <div className="px-3">
          {preferencePackage && (
            <PreferencePackageCard
              pkg={preferencePackage}
              isV2Market={isV2Market}
              plan={getPlanForPackage(preferencePackage)}
            />
          )}
        </div>

        {/* Add-ons Section */}
        {additionalServices.length > 0 && (
          <div className="mt-8 px-3">
            <div className="mb-4">
              <p className="text-3xl">
                <span className="font-serif italic text-evergreen">
                  You've got
                </span>{" "}
                <span className="font-bold text-soil">options</span>
              </p>
            </div>
            <p className="mb-4 text-base text-soil">
              With After, the power is in your hands. Our transparent pricing
              ensures you're not being overcharged for anything you don't want
              or need.
            </p>
            <p className="mb-4 text-base text-soil">
              Here are a few other add-ons available to meet your needs in this
              difficult time.
            </p>
            <div className="space-y-3">
              {additionalServices.map((service) => (
                <AddOnItem
                  key={service.variant_id}
                  label={service.title}
                  amount={service.pricing.amount}
                />
              ))}
            </div>
          </div>
        )}

        {/* Other Packages Section (V1 markets only) */}
        {!isV2Market && otherPackages.length > 0 && (
          <div className="mt-10 px-3">
            <div className="mb-4">
              <p className="text-3xl">
                <span className="font-serif italic text-evergreen">
                  Our other
                </span>{" "}
                <span className="font-bold text-soil">packages</span>
              </p>
            </div>
            <p className="mb-2 text-base text-soil">
              The {preferenceName} Package is most suited to your needs, but we
              have other options.
            </p>
            <p className="mb-6 text-base text-soil">
              You can begin {decedentName ? `${decedentName}'s ` : ""}
              arrangements now, and finalize your package later. No pressure.
            </p>
            <div className="space-y-4">
              {otherPackages.map((pkg) => (
                <OtherPackageCard
                  key={pkg.package}
                  pkg={pkg}
                  plan={getPlanForPackage(pkg)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      {preferencePackage && (
        <FixedFooterBar
          total={preferencePackage.total}
          onViewDetails={() => setIsPriceModalOpen(true)}
          placement={footerPlacement}
        />
      )}

      {/* Price Breakdown Modal */}
      {preferencePackage && (
        <PriceBreakdownModal
          pkg={preferencePackage}
          plan={getPlanForPackage(preferencePackage)}
          isOpen={isPriceModalOpen}
          onClose={() => setIsPriceModalOpen(false)}
        />
      )}
    </div>
  );
}
